import { Injectable } from '@angular/core';
import { translate } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, delay, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { Organization } from '../../models/organization.class';
import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { LocationsService } from '../../services/locations/locations.service';
import * as actions from '../actions';
import { AlertError, AlertSuccess, SetFlexFields } from '../actions';
import {
    getAssociatedOrganizationsMap$,
    getCurrentLogo$,
    getCurrentOrganizationId$,
    getFlexFields$,
    getLocationsMap$,
    getLogo$,
    getOrganizationChildHierarchy$,
    getOrganizationLocations$,
    getOrgIdVsNameMap$,
    getRelatedTenants$,
    getSelectedAppServiceId$,
    getSessionState$,
} from '../selectors';

@Injectable()
export class LocationEffects {
    private loading = {
        organization: {},
        logo: {},
        currentLogo: {},
    };
    private fetchedAssociatedOrgs = false;
    private failedFetch: { [key: string]: boolean } = {};

    constructor(
        private actions$: Actions,
        private store$: Store,
        private _api: ApiService,
        private _commonUtils: CommonUtilsService,
        private _locations: LocationsService
    ) {}

    sessionLogoutSuccessfull = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.SessionLogoutSuccessfull),
            mergeMap(() => {
                this.fetchedAssociatedOrgs = false;
                this.failedFetch = {};
                return [];
            })
        );
    });

    getOrganizationDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetLocation),
            delay(100),
            withLatestFrom(
                this.store$.select(getLocationsMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAssociatedOrganizationsMap$),
                this.store$.select(getSessionState$)
            ),
            mergeMap(([action, detailsMap, organizationId, organizationsMap, sessionState]) => {
                if (!sessionState) {
                    return [];
                }
                if (
                    !action.organizationId ||
                    (detailsMap?.[action.organizationId]?.addresses?.length > 0 && !action.update) ||
                    (this.failedFetch[action.organizationId] && !action.update)
                ) {
                    this.setAsCurrentOrganization(detailsMap?.[action.organizationId], action.setAsCurrentOrganization);
                    return [];
                }
                if (this.loading.organization[action.organizationId]) {
                    return [];
                }
                const organization = organizationsMap?.[action.organizationId];
                if (action.fromComponent === 'redirect' && organization) {
                    return [];
                }
                this.loading.organization[action.organizationId] = true;
                return this._api.locations.getOrganizationDetailsData(action.organizationId).pipe(
                    map(res => {
                        this.loading.organization[action.organizationId] = false;
                        this.failedFetch[action.organizationId] = false;
                        this.buildDisplayName(detailsMap, res);
                        this.setAsCurrentOrganization(res, action.setAsCurrentOrganization);
                        this.store$.dispatch(
                            actions.UpdateLocationInAssociationsTree({
                                organizationId,
                                organizations: [res],
                                parentId: organizationId,
                            })
                        );
                        return actions.UpdatedLocation({
                            organizationId,
                            organization: res,
                            updated: action.update,
                            parentId: organizationId,
                        });
                    }),
                    catchError(res => {
                        this.loading.organization[action.organizationId] = false;
                        this.failedFetch[action.organizationId] = true;
                        const isPermissionError =
                            res?.taxillaErrorCode === 'FORBIDDEN_ACCESS' ||
                            res?.status === 403 ||
                            res?.status === 'FORBIDDEN' ||
                            res?.msg === 'User does not have permission' ||
                            res?.error?.taxillaErrorCode === 'FORBIDDEN_ACCESS' ||
                            res?.error?.status === 403 ||
                            res?.error?.status === 'FORBIDDEN' ||
                            res?.error?.msg === 'User does not have permission' ||
                            (typeof res === 'string' &&
                                (res.includes('FORBIDDEN_ACCESS') || res.includes('User does not have permission'))) ||
                            (typeof res?.error === 'string' &&
                                (res.error.includes('FORBIDDEN_ACCESS') || res.error.includes('User does not have permission')));

                        if (isPermissionError) {
                            console.error('[FORBIDDEN_ACCESS] Location details fetch failed:', res);
                        }

                        if (action.fromComponent === 'redirect') {
                            if (isPermissionError) {
                                return of(actions.RedirectToBase());
                            }
                            this.store$.dispatch(actions.RedirectFailure());
                        }

                        const backendMessage = res?.msg || res?.message || res?.error?.msg || res?.error?.message;
                        const errorMessage = backendMessage
                            ? backendMessage
                            : isPermissionError
                            ? translate('You do not have permission to access this location.')
                            : translate('Failed to get organization details');

                        return of(AlertError({ message: errorMessage }));
                    })
                );
            })
        );
    });

    private setAsCurrentOrganization = (organization: Organization, setAsCurrentOrganization: boolean) => {
        if (setAsCurrentOrganization) {
            this.store$.dispatch(actions.SetCurrentOrganization({ organization }));
        }
    };

    getCurrentOrganizationDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetCurrentOrganization),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([_action, organizationId]) => {
                return of(
                    actions.GetLocation({
                        organizationId: organizationId,
                        update: false,
                    })
                );
            })
        );
    });

    getLocationsCodes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetLocationsCodes),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([_action, organizationId]) => {
                return this._api.locations.getLocationsCodesData().pipe(
                    map(res => {
                        return actions.SetLocationsCodes({
                            organizationId,
                            locationCodes: res,
                        });
                    }),
                    catchError(res => {
                        return of(AlertError({ message: res.msg || translate('Failed to get locations codes') }));
                    })
                );
            })
        );
    });

    addLocations = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.UpdateLocation),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getAssociatedOrganizationsMap$)),
            mergeMap(([action, organizationId, orgsMap]) => {
                const method = action?.payload?.id ? 'updateOrganizationDetails' : 'addLocation';
                const payload = action?.payload?.id ? { org: action.payload } : (action.payload as any);
                return this._api.locations?.[method]?.(payload).pipe(
                    map((res: Organization) => {
                        const nameChanged = action.payload.name !== orgsMap?.[action.payload.id]?.name;
                        !action.isLogoUpdated && this.store$.dispatch(AlertSuccess({ message: this.setMsg(res, action.payload) }));
                        if (method === 'addLocation') {
                            this.refetchAssociatedOrganizations();
                            return actions.SetLocation({
                                organizationId,
                                organization: res,
                                parentId: res?.parentId,
                            });
                        } else {
                            const orgsToUpdate: Organization[] = [];
                            if (nameChanged) {
                                const orgsMapClone = {
                                    ...orgsMap,
                                    [res?.id]: res,
                                };
                                [res, ...CommonUtilsService.cloneObject(this.getChildNodes(orgsMapClone, res?.id))].forEach(org => {
                                    this.buildDisplayName(orgsMapClone, org);
                                    orgsToUpdate.push(org);
                                });
                            } else {
                                orgsToUpdate.push(res);
                            }
                            this._locations.clearOrganizationDetailsCache(action.payload.id);
                            this.store$.dispatch(
                                actions.UpdateLocationInAssociationsTree({
                                    organizationId,
                                    organizations: orgsToUpdate,
                                    parentId: res?.parentId,
                                })
                            );
                            return actions.UpdatedLocation({
                                organizationId,
                                organization: res,
                                updated: action.update,
                                parentId: res?.parentId,
                            });
                        }
                    }),
                    catchError(e => {
                        return of(
                            AlertError({
                                message: e?.['msg'] || translate('Failed to update organization details'),
                                source: actions.LocationActionTypes.UPDATE_LOCATION,
                            })
                        );
                    })
                );
            })
        );
    });

    private refetchAssociatedOrganizations = () => {
        this.fetchedAssociatedOrgs = false;
        this.store$.dispatch(actions.GetAssociatedLocations());
    };

    private setMsg = (res, payload) => {
        if (res?.msg) {
            return res?.msg;
        } else if (!payload.fromOrganization) {
            return payload.id ? translate('Location details updated successfully') : translate('Location details added successfully');
        } else {
            return payload.id
                ? translate('Organization details updated successfully')
                : translate('Organization details added successfully');
        }
    };

    UpdateOrganizationLogo = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.UpdateLocationLogo),
            mergeMap(action => {
                return from(this._api.locations.uploadOrganizationLogo(action.payload)).pipe(
                    map((_res: any) => {
                        this.store$.dispatch(AlertSuccess({ message: translate('Organization details updated successfully') }));
                        this.store$.dispatch(actions.GetLocationLogo({ enableHierarchy: true, forFetch: true }));
                        return actions.GetLocationLogo({ enableHierarchy: false, forFetch: true });
                    }),
                    catchError(e => {
                        return of(AlertError({ message: e?.msg || translate('Failed to upload logo') }));
                    })
                );
            })
        );
    });

    DeleteOrganizationLogo = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.DeleteLocationLogo),
            mergeMap(() => {
                return from(this._api.locations.deleteOrganizationLogo()).pipe(
                    map((_res: any) => {
                        this.store$.dispatch(AlertSuccess({ message: translate('Organization details updated successfully') }));
                        this.store$.dispatch(actions.GetLocationLogo({ enableHierarchy: true, forFetch: true }));
                        return actions.GetLocationLogo({ enableHierarchy: false, forFetch: true });
                    }),
                    catchError(errorResponse => {
                        return of(AlertError({ message: errorResponse?.msg || translate('Failed to delete logo') }));
                    })
                );
            })
        );
    });

    GetOrganizationLogo = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetLocationLogo),
            delay(100),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getLogo$),
                this.store$.select(getCurrentLogo$)
            ),
            mergeMap(([action, organizationId, logo, currentLogo]) => {
                const loadingObject = action.enableHierarchy ? this.loading.logo : this.loading.currentLogo;
                if (
                    loadingObject[organizationId] ||
                    (!action.forFetch && (action.enableHierarchy ? logo !== undefined : currentLogo !== undefined))
                ) {
                    return [];
                }
                loadingObject[organizationId] = true;
                return from(this._api.locations.getOrganizationLogo(action.enableHierarchy)).pipe(
                    map(res => {
                        loadingObject[organizationId] = false;
                        return actions.SetLocationLogo({
                            currentOrgId: organizationId,
                            logoData: (res?.length > 0 && atob(res)) || '',
                            locationHierarchy: action.enableHierarchy,
                        });
                    }),
                    catchError(errorResponse => {
                        loadingObject[organizationId] = false;
                        if (errorResponse) {
                            return of(AlertError({ message: errorResponse }));
                        }
                        return of(
                            actions.SetLocationLogo({
                                currentOrgId: organizationId,
                                locationHierarchy: action.enableHierarchy,
                                logoData: undefined,
                            })
                        );
                    })
                );
            })
        );
    });

    locationActivated = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.ToggleLocation),
            withLatestFrom(this.store$.select(getOrganizationLocations$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, organizations, organizationId]) => {
                const organization = organizations.find(org => org.id === action.id);
                const method = action.enabled ? 'activateLocations' : 'deactivateLocations';
                return this._api.locations[method]({ orgId: action.id }).pipe(
                    map(res => {
                        this.store$.dispatch(AlertSuccess({ message: res as string }));
                        this.store$.dispatch(
                            actions.UpdateLocationInAssociationsTree({
                                organizationId,
                                organizations: [
                                    {
                                        ...organization,
                                        enabled: action.enabled,
                                    },
                                ] as Organization[],
                                parentId: organization?.parentId,
                            })
                        );
                        return actions.UpdatedLocation({
                            organizationId,
                            organization: {
                                ...organization,
                                enabled: action.enabled,
                            } as Organization,
                            updated: true,
                            parentId: organization?.parentId,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                );
            })
        );
    });

    GetFlexFields = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetFlexFields),
            withLatestFrom(this.store$.select(getFlexFields$)),
            mergeMap(([_action, flexFields]) => {
                if (flexFields?.length > 0) {
                    return [];
                }
                return from(this._api.locations.getAllFlexFields()).pipe(
                    map(flexFields => {
                        return SetFlexFields({ flexFields });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse || translate('Failed to GetFlexfields') })))
                );
            })
        );
    });

    GetAllOrganizations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetOrganizationLocations),
            withLatestFrom(this.store$.select(getOrganizationLocations$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([_action, organizationLocations, organizationId]) => {
                if (organizationLocations?.length > 1) {
                    return [];
                }
                return this._api.subscriptions
                    .searchAllOrganizations({
                        'parent.id': organizationId,
                    })
                    .pipe(
                        map(res => {
                            return actions.SetOrganizationLocations({
                                organizations: res,
                                organizationId,
                            });
                        }),
                        catchError(res => {
                            return of(AlertError({ message: res.msg || '' }));
                        })
                    );
            })
        );
    });

    GetAssociatedOrganizations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAssociatedLocations),
            mergeMap(() => {
                if (this.fetchedAssociatedOrgs) {
                    return [];
                }
                this.fetchedAssociatedOrgs = true;
                return this._api.locations.getOrganizationsPromise().pipe(
                    map(res => {
                        const orgMap = this.buildOrganizationsMap(CommonUtilsService.cloneObject(res));
                        this.buildNodes(orgMap);
                        this.buildDisplayNames(orgMap);
                        this.deleteParentRef(orgMap);
                        return actions.SetAssociatedLocations({
                            organizations: this.buildParentChildTree(orgMap),
                            nonModifiedOrgs: res,
                        });
                    }),
                    catchError(res => {
                        this.fetchedAssociatedOrgs = false;
                        return of(AlertError({ message: res.msg || '' }));
                    })
                );
            })
        );
    });

    private deleteParentRef = (orgMap: { [property: string]: Organization }) => {
        Object.keys(orgMap).forEach(id => {
            if (!orgMap[id]) {
                return;
            }
            const organization = orgMap[id];
            delete organization.parent;
        });
    };

    private buildOrganizationsMap = (list: Organization[]) => {
        const orgMap: { [property: string]: Organization } = {};
        list.forEach(organization => {
            orgMap[organization.id] = orgMap[organization.id] || organization;
        });
        return orgMap;
    };

    private buildNodes = (orgMap: { [property: string]: Organization }) => {
        Object.keys(orgMap).forEach(id => {
            const organization = orgMap[id];
            this.buildNode(orgMap, organization);
        });
    };

    private buildNode = (orgMap: { [property: string]: Organization }, organization: Organization) => {
        if (!organization.parentId) {
            return;
        }
        const parentOrganization = orgMap[organization.parentId] || this.buildNoAccessParentNodes(orgMap, organization);
        if (!parentOrganization) {
            return;
        }
        if (!parentOrganization.nextNodes) {
            parentOrganization.nextNodes = [];
        }
        parentOrganization.nextNodes.push(organization.id);
    };

    private buildNoAccessParentNodes = (orgMap: { [property: string]: Organization }, organization: Organization) => {
        if (!orgMap[organization.parentId] && organization.parent) {
            organization.parent.noAccess = true;
            orgMap[organization.parentId] = organization.parent;
            this.buildNode(orgMap, organization.parent);
            return orgMap[organization.parentId];
        }
    };

    private buildDisplayNames = (orgMap: { [property: string]: Organization }) => {
        Object.keys(orgMap).forEach(id => this.buildDisplayName(orgMap, orgMap[id]));
    };

    private buildDisplayName = (orgMap: { [property: string]: Organization }, org: Organization) => {
        org.displayTenantName = this.getOrganizationName(org, orgMap);
    };

    private getOrganizationName = (organization?: Organization, orgMap?: { [property: string]: Organization }, delimiter?: string) => {
        if (!organization) {
            return '';
        }
        if (!organization.parentId) {
            return organization.name;
        }
        return (
            this.getOrganizationName(organization.parent || orgMap?.[organization.parentId], orgMap) +
            (delimiter ? '  ' + delimiter + '  ' : '  |  ') +
            organization.name
        );
    };

    private buildParentChildTree = (orgMap: { [property: string]: Organization }) => {
        const parentTree: Organization[] = [];
        Object.keys(orgMap).forEach(id => {
            const parentId = orgMap[id].parentId;
            if (parentId) {
                if (!orgMap[parentId].childNodes) {
                    orgMap[parentId].childNodes = [];
                }
                orgMap[parentId].childNodes.push(orgMap[id]);
            } else {
                parentTree.push(orgMap[id]);
            }
        });
        return parentTree;
    };

    private getChildNodes = (orgMap: { [property: string]: Organization }, organizationId: string) => {
        const childNodes: Organization[] = [];
        Object.keys(orgMap)
            .filter(id => orgMap?.[id].parentId === organizationId)
            .forEach(id => {
                childNodes.push(orgMap?.[id], ...(this.getChildNodes(orgMap, id) || []));
            });
        return childNodes;
    };

    GetOrganizationLogoBySetCurrentOrg = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.SetCurrentOrganization),
            mergeMap(action => {
                if (!action.organization?.id) {
                    return [];
                }
                return of(actions.GetLocationLogo({ enableHierarchy: true }));
            })
        );
    });

    GetOrganizationChildHierarchy$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetOrganizationChildHierarchy),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getOrganizationChildHierarchy$)),
            mergeMap(([_action, organizationId, childHierarchy]) => {
                if (childHierarchy?.orgNodes?.length > 0) {
                    return of(
                        actions.SetOrganizationChildHierarchy({
                            orgId: organizationId,
                            childHierarchy: CommonUtilsService.cloneObject(childHierarchy),
                        })
                    );
                }
                return this._api.organization.getOrganizationChildHierarchy(organizationId).pipe(
                    map(res => {
                        return actions.SetOrganizationChildHierarchy({
                            orgId: organizationId,
                            childHierarchy: res,
                        });
                    }),
                    catchError(res => {
                        return of(AlertError({ message: res?.msg || 'Failed due to server error' }));
                    })
                );
            })
        );
    });

    getOrganizationNamesByIds$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetOrganizationNamesByIds),
            withLatestFrom(this.store$.select(getOrgIdVsNameMap$)),
            mergeMap(([action, orgIdsVsOrgNames]) => {
                const orgIds: string[] = action.orgIds.filter(id => !orgIdsVsOrgNames.hasOwnProperty(id));
                if (orgIds?.length === 0) {
                    return [];
                }
                const data = {
                    orgIds,
                    noAlerts: action.noAlerts,
                };
                return this._api.locations.getOrgNamesByIdsObservable(data).pipe(
                    map(res => {
                        return actions.SetOrganizationNamesByIds({
                            orgIdsVsOrgNames: res,
                        });
                    }),
                    catchError(res => {
                        return of(AlertError({ message: res?.msg }));
                    })
                );
            })
        );
    });

    GetRelatedTenants$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetRelatedTenants),
            withLatestFrom(this.store$.select(getRelatedTenants$), this.store$.select(getSelectedAppServiceId$)),
            mergeMap(([action, relatedTenants, serviceId]) => {
                if (relatedTenants?.length > 0) {
                    return [];
                }
                this.store$.dispatch(actions.ShowTenantsLoader({ loading: true }));
                return from(this._api.locations.getRelatedTenantsObservable({ orgId: action.orgId, serviceId: serviceId })).pipe(
                    map((res: any) => {
                        const tenants = [];
                        res.response &&
                            res.response.forEach(tenant => {
                                const obj = {
                                    id: '',
                                    displayTenantName: '',
                                    orgLoginId: '',
                                    name: '',
                                };
                                obj.id = tenant.id;
                                obj.orgLoginId = tenant.orgLoginId;
                                obj.name = tenant.name + ' (' + tenant.orgLoginId + ')';
                                obj.displayTenantName = this._commonUtils.getOrganizationName(tenant) + ' (' + tenant.orgLoginId + ')';
                                tenants.push(obj);
                            });
                        this.store$.dispatch(actions.ShowTenantsLoader({ loading: false }));
                        return actions.SetRelatedTenants({
                            relatedTenants: tenants,
                            serviceId: serviceId,
                        });
                    }),
                    catchError(res => {
                        this.store$.dispatch(actions.ShowTenantsLoader({ loading: false }));
                        return of(AlertError({ message: res?.msg }));
                    })
                );
            })
        )
    );
}
